/**
 * A multi-line text control that behaves like `@wend/ui`'s `Input`.
 *
 * WHY THE REASON FIELD IS NOT AN `<Input>`. A reason may run to 500 characters
 * (`MAX_ADMIN_REASON_LENGTH`, derived from `MAX_CREDIT_NOTE_LENGTH`, because a
 * top-up's reason is copied onto the ledger entry's note). A single-line input
 * that scrolls sideways past character forty does not merely look cramped: it
 * hides what has already been typed, so the person cannot re-read their own
 * sentence before committing it to an append-only table. The size of the box is
 * the strongest signal this screen gives about how much of an explanation is
 * wanted, and a one-line box asks for one word.
 *
 * WHY IT IS A COPY, AND WHAT SHOULD HAPPEN TO IT. `apps/web` has the same
 * component, and its header says plainly that the moment a second screen wants
 * one is the moment it belongs in `@wend/ui`. That moment is now, and this is
 * not it: this change owns `apps/admin` and nothing else, and reaching into the
 * design system from here would put a shared package's history inside an admin
 * slice. **This is a deliberate hole, marked rather than hidden** — the next
 * change that may touch `packages/ui` should promote `Textarea` and delete both
 * copies, and until then the two must be kept identical, because a divergence
 * between them is a control that focuses differently on two screens for no
 * reason anyone can name.
 *
 * It is not a fork of `Input`: it wires itself to a surrounding `<FormField>`
 * through the same exported `useFieldProps` hook the design system offers
 * app-authored controls, so the label, the description, the error and the
 * invalid state all still come from the field.
 */
import { cn, useFieldProps } from '@wend/ui';
import { forwardRef, type TextareaHTMLAttributes } from 'react';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Force the error styling without a surrounding `<FormField>`. */
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, id, invalid, 'aria-describedby': describedBy, 'aria-invalid': ariaInvalid, ...rest },
  ref,
) {
  // An explicit aria-invalid always wins over the field's; `undefined` lets the
  // surrounding FormField decide. Same rule as Input.
  const resolvedInvalid: boolean | undefined =
    ariaInvalid === undefined
      ? invalid === true
        ? true
        : undefined
      : ariaInvalid !== false && ariaInvalid !== 'false';

  const fieldProps = useFieldProps({
    id,
    'aria-describedby': describedBy,
    'aria-invalid': resolvedInvalid,
  });

  return (
    <textarea
      ref={ref}
      className={cn(
        'block w-full resize-y rounded-base border border-line-strong bg-ink/55 px-3.5 py-2.5',
        'text-[0.95rem] leading-normal text-text placeholder:text-muted',
        'transition-[background-color,border-color,box-shadow] duration-150 ease-wend',
        'hover:border-white/25',
        'focus-visible:border-brand-a focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-b',
        'disabled:cursor-not-allowed disabled:opacity-55',
        'aria-[invalid=true]:border-bad aria-[invalid=true]:focus-visible:outline-bad',
        className,
      )}
      {...fieldProps}
      {...rest}
    />
  );
});
